import { ageAtDeath } from "@shared/age";
import { boardStep, boardTiles } from "@shared/interview/board";
import { TilePicker, type NoneTile, type Tile, type TileSelection } from "./TilePicker";

/**
 * s221 Lane 1, mechanism 6 — TILES CARRIED IN FROM THE SCREEN BEFORE (rule 42).
 *
 * "A person the plan already knows is carried in, never asked again." The day-to-day screen's tiles
 * ARE the children and dependants named on the screen before — their names, read-only, not a fresh
 * "Name them". This builds those tiles from the rows the plan already holds and hands them to the
 * shared TilePicker (mechanism 1), so a carried-in tile behaves exactly like any other.
 *
 * Who is carried in (SPEC `daily` step: "only when the screen before named a child under 18 or a
 * dependant"): every dependant, and every child under 18. A child with no date of birth IS carried in
 * — the plan cannot tell, and leaving them out would hide a person the planner may need to answer for.
 * The age is the shared `ageAtDeath` (whole years between two dates), measured to today; it only
 * decides which tiles are OFFERED and never hides or changes a stored row.
 */

export type KnownPerson = {
  rowId: string;
  name: string;
  kind: "child" | "dependant";
  /** ISO `YYYY-MM-DD`, or empty. */
  dob?: string | null;
};

/** The SPEC board's person marks — the `kids` step's `child` and `dep` tiles (s222: read from the board
 *  data, no longer re-typed). ONE place, used by every interview surface that draws a child or a
 *  dependant: tiles, row leads, note cards. */
const markOf = (id: string) => {
  const t = boardTiles(boardStep("kids")).tiles.find((x) => x.id === id);
  if (!t?.mark || !t.color) throw new Error(`board: the kids tile "${id}" has no mark or colour`);
  return { mark: t.mark, color: t.color };
};
export const PERSON_MARK = { child: markOf("child"), dependant: markOf("dep") } as const;
/** The carried-in tile's sub-line — the board's `daily` placeholder tiles declare it. */
const CARRIED_SUB = boardTiles(boardStep("daily")).tiles[0]?.sub ?? "";
const CHILD = PERSON_MARK.child;
const CARE = PERSON_MARK.dependant;

/** One person: a dependant, or a child under 18 or of unknown age. Pure; `today` is ISO `YYYY-MM-DD`.
 *  The ONE under-18 rule for the interview — the children screen's backup-guardian cell asks it too. */
export function isStillLookedAfter(p: Pick<KnownPerson, "kind" | "dob">, today: string): boolean {
  if (p.kind === "dependant") return true;
  const age = ageAtDeath(p.dob, today);
  return age === null || age < 18;
}

/** The people the day-to-day screen asks about. Pure; `today` is ISO `YYYY-MM-DD`.
 *  🔴 s221 Lane 1c, fix 7: a person with NO NAME is not carried in — a blank row is not a person, and it
 *  must never seed a tile, a row, or anything after it. */
export function peopleStillLookedAfter(people: readonly KnownPerson[], today: string): KnownPerson[] {
  return people.filter((p) => p.name.trim() !== "" && isStillLookedAfter(p, today));
}

/** Tiles for the carried-in people, keyed by their row id. Pure. */
export function carriedInTiles(people: readonly KnownPerson[]): Tile[] {
  // Fix 7: a nameless person is never a tile (the old "(no name yet)" tile is gone).
  return people.filter((p) => p.name.trim() !== "").map((p) => ({
    id: p.rowId,
    label: p.name.trim(),
    sub: CARRIED_SUB,
    ...(p.kind === "child" ? CHILD : CARE),
  }));
}

/**
 * s237 (chapter three PR 2; D-237.22) — tiles carried in from ANOTHER SCREEN'S ROWS, not only people: one tile per named
 * row of the screen before (Work's jobs → Coverage's "Health and dental — <employer>"). The generalisation of
 * `carriedInTiles` the plan's §B names; chapter three PR 3 is its first user. Pure.
 *   • each row gives one tile, id `<base id>@<row id>`, label `<base label> — <the row's name>`;
 *   • a row with no name yet reads `<base label> — <words.unnamed>` ("through your job");
 *   • with no rows at all, ONE tile reads `<base label> <words.noRows>` ("through a job"), id the base id.
 */
export type CarriedRow = { rowId: string; name: string };
export function carriedRowTiles(base: Tile, rows: readonly CarriedRow[], words: { unnamed: string; noRows: string }): Tile[] {
  if (rows.length === 0) return [{ ...base, label: `${base.label} ${words.noRows}` }];
  return rows.map((r) => ({ ...base, id: `${base.id}@${r.rowId}`, label: `${base.label} — ${r.name.trim() || words.unnamed}` }));
}

export function CarriedInTiles({
  people,
  today,
  selection,
  onChange,
  none,
  testIdPrefix = "interview-carried",
}: {
  people: readonly KnownPerson[];
  today: string;
  selection: TileSelection;
  onChange: (next: TileSelection, changed: string) => void;
  none?: NoneTile;
  testIdPrefix?: string;
}) {
  const tiles = carriedInTiles(peopleStillLookedAfter(people, today));
  if (tiles.length === 0) {
    return (
      <p className="text-[14px] text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
        Nobody named on the screen before still needs looking after.
      </p>
    );
  }
  return <TilePicker tiles={tiles} selection={selection} onChange={onChange} none={none} columns={3} testIdPrefix={testIdPrefix} />;
}
